import React from 'react';
import { ColorfulHorizontalLine } from '@epcbuilder/lib/components/Lines';
import PageSection from '../PageSection';

const sectionContainer = 'mx-6 sm:mx-8 md:mx-6 xl:mx-auto xl:w-11/12 xl:max-w-screen-2xl';
const headingClass = 'text-md text-2xl font-bold leading-tight md:text-[48px] xl:text-[64px]';
const subheadingClass = 'font-jost mb-4 text-[20px] font-medium md:text-[24px]';
const paragraphClass = 'leading-[28px] sm:leading-[30px] xl:text-[18px] xl:leading-9';

const TickIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="size-6 lg:size-9" viewBox="0 0 36 36" fill="none">
    <path
      d="M1.22852 17.6416C6.24531 23.0697 11.1095 27.9564 15.7853 34.1369C20.8689 24.026 26.072 13.8799 34.6572 2.89213L32.3439 1.83252C25.0947 9.52049 19.4627 16.7978 14.5689 25.4463C11.1658 22.3806 5.66594 18.0424 2.30781 15.8135L1.22852 17.6416Z"
      fill="#70AA80"
    />
  </svg>
);

const benefits = [
  {
    title: 'Prevent damp & mould',
    desc: 'Reducing condensation buildup.',
  },
  {
    title: 'Protect your insulation',
    desc: 'Ensures it doesn’t become a trap for moisture.',
  },
  {
    title: 'Improve indoor air quality',
    desc: 'Removes pollutants and maintains a healthy environment.',
  },
];

const installationSteps = [
  {
    step: '01',
    title: 'Cavity Wall Insulation Installation',
    desc: 'We inject high-performance insulation into your walls.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <path
          d="M48 0.751953L37.8151 0.76234V10.9348H48V0.751953ZM36 0.764054L12 0.788961V10.9348H36V0.764054ZM10.1849 0.790877L0 0.801365V10.9348H10.1849V0.790877ZM0 12.7499V23.8424H23.0924V12.7499H0ZM24.9076 12.7499V23.8424H48V12.7499H24.9076ZM0 25.6575V36.7499H10.1849V25.6575H0ZM12 25.6575V36.7499H36V25.6575H12ZM37.8151 25.6575V36.7499H48V25.6575H37.8151ZM0 38.5651V48.7479L23.0924 48.7241V38.5651H0ZM24.9076 38.5651V48.7224L48 48.6985V38.5651H24.9076Z"
          fill="#E76F51"
        />
      </svg>
    ),
    color: '#E76F51',
  },
  {
    step: '02',
    title: 'Ventilation Installation',
    desc: 'Any necessary ventilation, such as extractor fans or trickle vents, is installed.',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" width="48" height="49" viewBox="0 0 48 49" fill="none">
        <g clipPath="url(#clip0_4512_49975)">
          <path
            d="M24.2438 0.25C24.0844 0.25 23.925 0.259375 23.7656 0.296875C15.75 1.84375 10.8375 9.84063 12.2344 17.9969C12.5063 19.5906 13.0219 21.0812 13.725 22.4406L3 21.25H2.75625C1.2375 21.25 0 22.4875 0 24.0062C0 24.1656 0.009375 24.325 0.046875 24.4844C1.59375 32.5 9.59063 37.4125 17.7469 36.0156C19.3406 35.7438 20.8312 35.2281 22.1906 34.525L21 45.25V45.4938C21 47.0125 22.2375 48.25 23.7562 48.25C23.9156 48.25 24.075 48.2406 24.2344 48.2031C32.25 46.6562 37.1625 38.6594 35.7656 30.5031C35.4938 28.9094 34.9781 27.4187 34.275 26.0594L45 27.25H45.2438C46.7625 27.25 48 26.0125 48 24.4938C48 24.3344 47.9906 24.175 47.9531 24.0156C46.4062 16 38.4094 11.0875 30.2531 12.4844C28.6594 12.7563 27.1687 13.2719 25.8094 13.975L27 3.25V3.00625C27 1.4875 25.7625 0.25 24.2438 0.25ZM24 21.25C24.7956 21.25 25.5587 21.5661 26.1213 22.1287C26.6839 22.6913 27 23.4544 27 24.25C27 25.0456 26.6839 25.8087 26.1213 26.3713C25.5587 26.9339 24.7956 27.25 24 27.25C23.2044 27.25 22.4413 26.9339 21.8787 26.3713C21.3161 25.8087 21 25.0456 21 24.25C21 23.4544 21.3161 22.6913 21.8787 22.1287C22.4413 21.5661 23.2044 21.25 24 21.25Z"
            fill="#F0935C"
          />
        </g>
        <defs>
          <clipPath id="clip0_4512_49975">
            <rect width="48" height="48" fill="white" transform="translate(0 0.25)" />
          </clipPath>
        </defs>
      </svg>
    ),
    color: '#F0935C',
  },
];
const CavityWallInsulationPage = () => {
  return (
    <div className="flex min-h-screen flex-col gap-10 bg-white lg:gap-12 xl:gap-16">
      <div id="finance-intro" className={`${sectionContainer} mt-10 flex flex-col gap-4 md:mt-12 lg:gap-6`}>
        <h2 className={headingClass}>Cavity Wall Insulation Information</h2>
        <p className="font-jost text-[#516B75] md:text-[20px] xl:text-[24px]">
          Enhancing energy efficiency and home comfort
        </p>
      </div>

      <PageSection title="About ThermaBead" containerClassName={sectionContainer}>
        <div className="flex flex-col gap-4 xl:gap-6">
          <p className={paragraphClass}>
            ThermaBead is a leading provider of high-performance, highly efficient cavity wall insulation. Their
            advanced insulation solutions are designed to reduce heat loss, improve energy efficiency, and enhance home
            comfort.
          </p>
        </div>
      </PageSection>

      <div className="bg-[#F8F9FA]">
        <div className={`${sectionContainer} m-6 flex flex-col gap-6 md:flex-row md:items-center`}>
          <div className="md:w-1/2">
            <h2 className="mb-2 font-[unset] text-lg font-black uppercase sm:mb-4 md:text-[22px] xl:text-2xl">
              About Closed Cell Polystyrene Beads{' '}
            </h2>
            <ColorfulHorizontalLine width="10rem" />
            <p className={`mr-6 mt-4 md:max-w-[552px] ${paragraphClass}`}>
              Closed Cell Polystyrene Beads act like a tea cosy, trapping heat inside your home and keeping it cosy and
              warm. This insulation method provides excellent thermal efficiency while preventing cold air from entering
              through the walls.
            </p>
          </div>
          <div className="flex flex-col gap-4 sm:gap-6 md:w-1/2 md:px-6">
            {['therma-bead-logo.png', 'cavity-wall-insulation.jpg'].map((img, i) => (
              <div
                key={i}
                className={`
        ${i === 0 ? 'h-[50px] max-w-[130px] bg-contain bg-left' : 'h-[200px] max-w-[450px] rounded-[20px] bg-contain bg-left'}
        bg-no-repeat
      `}
                style={{
                  backgroundImage: `url('/images/cavity-wall-insulation/${img}')`,
                  backgroundColor: '#F8F9FA',
                  backgroundBlendMode: 'darken',
                }}
              />
            ))}
            <div>
              <p className="font-bold leading-7 xl:text-[18px]">ThermaBead</p>
              <p className="leading-7 text-[#516B75] xl:text-[18px]">Closed Cell Polystyrene Beads</p>
            </div>
          </div>
        </div>
      </div>

      <PageSection title="Ventilation & Your Home" containerClassName={sectionContainer}>
        <div className="flex flex-col gap-8 md:flex-row lg:gap-12 xl:gap-20">
          <div className="md:w-1/2">
            <p className={subheadingClass}>Why Ventilation Is Important</p>
            <p className={`mb-4 ${paragraphClass}`}>
              Proper ventilation plays a crucial role in maintaining a healthy home environment. It helps to:
            </p>
            <ul className="space-y-4">
              {benefits.map(({ title, desc }) => (
                <li key={title} className="flex items-center gap-3">
                  <div className="w-8">
                    <TickIcon />
                  </div>
                  <div>
                    <p className={`font-semibold ${paragraphClass}`}>{title}</p>
                    <p className={`text-[#516B75] ${paragraphClass}`}>{desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:w-1/2">
            <p className={subheadingClass}>Ventilation During Installation</p>
            <p className={paragraphClass}>
              When installing Cavity Wall Insulation, additional ventilation may be required. This could include:
            </p>
            <ul className="list-inside list-disc space-y-2">
              <li className={paragraphClass}>Extractor fans - To enhance airflow and reduce humidity.</li>
              <li className={paragraphClass}>
                Trickle vents - Installed on windows to allow continuous air circulation.
              </li>
            </ul>
            <p className={paragraphClass}>
              The type of ventilation needed depends on your property, and our team will assess this during
              installation.
            </p>
          </div>
        </div>
      </PageSection>

      <PageSection title="Installation" containerClassName={sectionContainer}>
        <div className="mb-10 flex flex-col gap-10 lg:flex-row xl:mb-20 xl:gap-20">
          <div className="lg:w-1/2">
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Installation Steps</p>
            <div className="flex flex-col gap-4">
              {installationSteps.map(({ step, title, desc, icon, color }) => (
                <div
                  key={step}
                  className="flex w-full items-center gap-4 rounded-[10px] bg-white p-4 lg:rounded-[20px]"
                  style={{
                    boxShadow: '0px 0px 10px 0px rgba(21, 53, 66, 0.15)',
                    alignSelf: 'stretch',
                  }}
                >
                  <div className="mr-4 text-center">
                    <p className="text-[17px] font-extrabold text-[#748991] xl:text-[19px]">STEP</p>
                    <p
                      className="text-[40px] font-[900] leading-[50px] xl:text-[48px]"
                      style={{
                        color,
                      }}
                    >
                      {step}
                    </p>
                  </div>
                  <div className="flex-1">
                    <p className={`font-semibold ${paragraphClass}`}>{title}</p>
                    <p className={`text-[#516B75] ${paragraphClass}`}>{desc}</p>
                  </div>
                  <div className="ml-4 hidden text-2xl sm:flex">{icon}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:w-1/2">
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Installation Duration</p>
            <p className={`mb-6 ${paragraphClass}`}>
              Installing Cavity Wall Insulation typically takes 1-2 days, including any required ventilation work.
            </p>
            <p className="font-jost mb-4 text-[20px] font-medium md:text-[24px]">Warranties & Guarantees</p>
            <ul className="space-y-4">
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Manufacturer Warranty</p>
              </li>
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Material & Workmanship Guarantee</p>
              </li>
              <li className="flex items-center gap-3">
                <TickIcon />
                <p>Insurance Backed Guarantee</p>
              </li>
            </ul>
          </div>
        </div>
      </PageSection>
    </div>
  );
};

export default CavityWallInsulationPage;
